import { useState } from "react";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  SkipNext as IconSkipNext,
  NavigateNext as IconNavigateNext,
  NavigateBefore as IconNavigateBefore,
  SkipPrevious as IconSkipPrevious,
  ZoomIn as IconZoomIn,
  ZoomOut as IconZoomOut,
} from "@mui/icons-material";

import {
  DetectionHistogramState,
  DetectionAbacusState,
  DetectionFilterInput,
} from "../../redux/detections/detection-types";
import FlexCol from "../common/FlexCol";
import FlexRow from "../common/FlexRow";
import ResizableSplitPanel from "../common/ResizableSplitPanel";
import { DetectionOverview } from "./DetectionOverview";
import DetectionAbacus from "./DetectionAbacus";
import {
  ZoomOverride,
  AbacusGroupMode,
  SelectionInterval,
  TimeRangeTs,
  TxSelection,
  EntitiesInScope,
} from "./detection-types";
import { AbacusPlot } from "../../helpers/glossary";
import { SpinnerInnovasea } from "../../fathom-brella";
import { DetectionTableAG } from "./table/DetectionTableAG";
import { Animal } from "../../redux/animals/animals-types";
import { getAnimalNamesWithTaggingEvents } from "./detection-helper";

type Mode = "table" | "abacus";

/** having this defined within the function prevented the brush interval from working properly */
const VEGA_ACTIONS = {
  export: { png: true, svg: false },
  compiled: false,
  editor: false,
  source: false,
};

const useStyles = makeStyles(theme => ({
  visArea: {
    padding: theme.spacing(1),
    flexGrow: 1,
    overflow: "hidden",
    position: "relative",
  },
  toolBarGroup: {
    flexWrap: "wrap",
  },
  toolBarCaption: {
    paddingRight: theme.spacing(1),
    whiteSpace: "nowrap",
  },
}));

export function NavDivider() {
  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        borderRight: "1px solid lightgray",
        marginLeft: "8px",
        marginRight: "8px",
      }}
    ></div>
  );
}

export function DetectionVisualizer({
  columnFilters,
  downloadDetections,
  displayMode = "abacus",
  setDisplayMode,
  histogram,
  abacus,
  includeMetadata,
  useTimeCorrection,
  overviewBinSeconds,
  filterInterval,
  histogramTimeRange,
  selectionInterval,
  setSelectionInterval,
  abacusGroupBy,
  setAbacusGroupBy,
  animals,
  fileIdToNameMap,
  txs,
}: {
  columnFilters: DetectionFilterInput;
  downloadDetections: () => void;
  displayMode: Mode;
  setDisplayMode: (mode: Mode) => void;
  histogram: DetectionHistogramState;
  abacus: DetectionAbacusState;
  includeMetadata: boolean;
  useTimeCorrection: boolean;
  overviewBinSeconds: number;
  filterInterval: TimeRangeTs;
  histogramTimeRange: TimeRangeTs;
  selectionInterval: SelectionInterval;
  setSelectionInterval: (interval: SelectionInterval) => void;
  abacusGroupBy: AbacusGroupMode;
  setAbacusGroupBy: (groupBy: AbacusGroupMode) => void;
  animals: Animal[];
  fileIdToNameMap: Map<string, string>;
  txs: TxSelection[];
}) {
  // entitiesInScope dictates which animals, serial, full ids 
  // are displayed on y-axis in abacus
  const entitiesInScope: EntitiesInScope = {
    receiverSerials: columnFilters.includeReceiverSerials || [],
    // We don't specify a txs filter when querying for all txs,
    // but we will need to specify the full list when displaying
    // it in the abacus plot
    transmitterIDs: columnFilters.includeTransmitterIDs || txs.map(tx => tx.displayId),
    // Animals with tagging events can potentially have detections
    // and we want them to have a row in abacaus plot
    animalNames: getAnimalNamesWithTaggingEvents(animals),
  };
  const classes = useStyles();
  const [zoomOverride, setZoomOverride] = useState(null as ZoomOverride);

  const histogramReady = histogram.status === "COMPLETE";
  const histogramSelection =
    histogram.status === "COMPLETE" &&
    !(
      !selectionInterval ||
      selectionInterval.length === 0 ||
      selectionInterval[0] == selectionInterval[1]
    );

  const selectionExceedsTimeframe = filterInterval.max <= (selectionInterval?.[1] || 0);
  const selectionPreceedsTimeframe = filterInterval.min >= (selectionInterval?.[0] || 0);
  const selectionAtBeginning = filterInterval.min === (selectionInterval?.[0] || 0);
  const selectionAtMax =
    selectionInterval?.length &&
    selectionInterval[1] - selectionInterval[0] >= filterInterval.max - filterInterval.min
      ? true
      : false;

  const needSelectionMsg =
    "Please make a selection (click and drag) on the overview plot below first";

  return (
    <FlexCol fullWidth fullHeight>
      <ResizableSplitPanel
        direction="vertical"
        firstInit={150}
        firstMin={150}
        firstContent={
          <FlexCol className={classes.visArea} style={{ paddingBottom: 0 }}>
            <FlexRow style={{ paddingBottom: 8 }}>
              {
                <>
                  <FlexRow vAlign="center" className={classes.toolBarGroup}>
                    <Typography variant="caption" className={classes.toolBarCaption}>
                      Visualize by:
                    </Typography>
                    <RadioGroup
                      row
                      value={displayMode}
                      onChange={e => setDisplayMode(e.target.value as Mode)}
                    >
                      <FormControlLabel
                        value="abacus"
                        control={<Radio size="small" />}
                        label={AbacusPlot.title}
                      />
                      <FormControlLabel
                        value="table"
                        control={<Radio size="small" />}
                        label="Table"
                      />
                    </RadioGroup>
                  </FlexRow>
                  <NavDivider />
                </>
              }
              <FlexRow vAlign="center" className={classes.toolBarGroup}>
                <Typography variant="caption" className={classes.toolBarCaption}>
                  Selected time frame:
                </Typography>
                <FlexRow
                  itemSpacing={1}
                  paddingLevel={0.5}
                  vAlign="center"
                  title={
                    histogramReady
                      ? "You can select an area of the overview histogram (click and drag) to specify a time frame for the visualization below it"
                      : "Please wait for data to load"
                  }
                >
                  <Button
                    variant="outlined"
                    size="small"
                    style={{ textTransform: "none" }}
                    onClick={() => setZoomOverride("in")}
                    disabled={!histogramReady}
                  >
                    <IconZoomIn />
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    style={{ textTransform: "none" }}
                    onClick={() => setZoomOverride("out")}
                    disabled={!histogramSelection || selectionAtMax}
                  >
                    <IconZoomOut />
                  </Button>
                  <span
                    title={`${
                      !histogramSelection
                        ? needSelectionMsg
                        : selectionAtBeginning
                          ? "Already at the beginning"
                          : "Move the selection window to the beginning of the data"
                    }`}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      style={{ textTransform: "none" }}
                      onClick={() => setZoomOverride("beginning")}
                      disabled={!histogramSelection || selectionAtBeginning}
                    >
                      <IconSkipPrevious />
                    </Button>
                  </span>
                  <span
                    title={`${
                      !histogramSelection
                        ? needSelectionMsg
                        : selectionPreceedsTimeframe
                          ? "Cannot go previous since the selection extends before the beginning of the data"
                          : "Move the selection window to the previous adjacent time frame"
                    }`}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      style={{ textTransform: "none" }}
                      onClick={() => setZoomOverride("prev")}
                      disabled={!histogramSelection || selectionPreceedsTimeframe}
                    >
                      <IconNavigateBefore />
                    </Button>
                  </span>
                  <span
                    title={`${
                      !histogramSelection
                        ? needSelectionMsg
                        : selectionExceedsTimeframe
                          ? "Cannot go next since the selection extends past the end of the time frame"
                          : "Move the selection window to the next adjacent time frame"
                    }`}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      style={{ textTransform: "none" }}
                      onClick={() => setZoomOverride("next")}
                      disabled={!histogramSelection || selectionExceedsTimeframe}
                    >
                      <IconNavigateNext />
                    </Button>
                  </span>
                  <span
                    title={`${
                      !histogramSelection
                        ? needSelectionMsg
                        : selectionExceedsTimeframe
                          ? "Already at the end"
                          : "Move the selection window to the end of the time frame"
                    }`}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      style={{ textTransform: "none" }}
                      onClick={() => setZoomOverride("end")}
                      disabled={!histogramSelection || selectionExceedsTimeframe}
                    >
                      <IconSkipNext />
                    </Button>
                  </span>
                  <span
                    title={`${!histogramSelection ? needSelectionMsg : "Clear the selection window"}
                    `}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      style={{ textTransform: "none" }}
                      onClick={() => setZoomOverride("clear")}
                      disabled={!histogramSelection}
                    >
                      Clear
                    </Button>
                  </span>
                </FlexRow>
              </FlexRow>
            </FlexRow>
            {histogram.status === "UNLOADED" ? (
              <div></div>
            ) : ["INITIAL_LOADING"].includes(histogram.status) ? (
              <FlexCol fullWidth fullHeight vAlign="center" hAlign="center">
                <CircularProgress />
              </FlexCol>
            ) : ["LOADING", "COMPLETE"].includes(histogram.status) ? (
              <DetectionOverview
                loading={histogram.status === "LOADING"}
                countPerInterval={histogram.countPerInterval}
                onSelectInterval={setSelectionInterval}
                actions={VEGA_ACTIONS}
                zoomOverride={zoomOverride}
                setZoomOverride={setZoomOverride}
                binSeconds={overviewBinSeconds}
                filterInterval={filterInterval}
                histogramTimeRange={histogramTimeRange}
              />
            ) : (
              <div>An unexpected error occured</div>
            )}
          </FlexCol>
        }
        secondContent={
          <FlexCol className="ag-theme-material" fullHeight fullWidth style={{ paddingTop: 0 }}>
            {displayMode === "table" && (
              <DetectionTableAG
                useTimeCorrection={useTimeCorrection}
                includeMetadata={includeMetadata}
                animals={animals}
                columnFilters={columnFilters}
                downloadDetections={downloadDetections}
                fileIdToNameMap={fileIdToNameMap}
              />
            )}
            {displayMode === "abacus" &&
              (abacus.status === "UNLOADED" ? (
                <div></div>
              ) : ["INITIAL_LOADING"].includes(abacus.status) ? (
                <SpinnerInnovasea />
              ) : ["LOADING", "COMPLETE"].includes(abacus.status) ? (
                <DetectionAbacus
                  includeMetadata={includeMetadata}
                  loading={abacus.status === "LOADING"}
                  selectionInterval={selectionInterval}
                  detectionCounts={abacus.countPerGroup}
                  actions={VEGA_ACTIONS}
                  setZoomWindow={setZoomOverride}
                  filterInterval={filterInterval}
                  histogramTimeRange={histogramTimeRange}
                  entitiesInScope={entitiesInScope}
                  abacusGroupBy={abacusGroupBy}
                  setAbacusGroupBy={setAbacusGroupBy}
                  animals={animals}
                />
              ) : (
                <div>An unexpected error occured</div>
              ))}
          </FlexCol>
        }
      />
    </FlexCol>
  );
}
